import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { reduxConnect } from '../../common';
import styled from 'styled-components';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { DownloadIcon } from '../common/icons/downloadIcon';
import { AlertIcon } from '../common/icons/alertIcon';
import { TwoLayersIcon } from '../common/icons/twoLayersIcon';
import { ClockIcon } from '../common/icons/clockIcon';

const android = 'ANDROID';
const ios = 'IOS';
const macos = 'MACOS';

const mapStoreStateToProps = storeState => {
    const appState = storeState.appState && storeState.appState.appState || {};
    const {
        os,
        iosAppDownloadLink,
        macosAppDownloadLink,
        androidAppDownloadLink,
        desktopAppDownloadLink,
        supportEmailAddress
    } = appState;

    const downloadLinks = {
        ios: iosAppDownloadLink,
        macos: macosAppDownloadLink,
        android: androidAppDownloadLink,
        general: desktopAppDownloadLink
    };

    return {
        os,
        downloadLinks,
        supportEmailAddress
    };
};

const mapDispatchToProps = {};

@reduxConnect(mapStoreStateToProps, mapDispatchToProps)
@withScreenSize
export class AppDownloadScreen extends Component {
    static displayName = 'AppDownloadScreen';
    static propTypes = {
        os: PropTypes.string
    };
    static defaultProps = {
        os: ''
    }

    constructor(props) {
        super(props);
    }

    render() {
        const { isDesktop, isMobile, os, downloadLinks, supportEmailAddress } = this.props;
        const isAndroid = os.toUpperCase() === android;
        const isIOS = !isAndroid && os.toUpperCase() === ios;
        const isMacOS = !isAndroid && !isIOS && os.toUpperCase() === macos;
        const isGeneral = !isAndroid && !isIOS && !isMacOS;
        const downloadLink = downloadLinks[os.toLowerCase()] || downloadLinks.general;
        let deviceText = '';
        let deviceImageUrl= '';
        if (isAndroid) {
            deviceText = 'phone';
            deviceImageUrl = 'images/appdownload/androidApp.png';
        } else if (isIOS) {
            deviceText = 'phone';
            deviceImageUrl = 'images/appdownload/iosApp.png';
        } else if (isMacOS) {
            deviceText = 'Mac';
            deviceImageUrl = 'images/appdownload/macbookApp.png';
        }

        return <ScreenContainer>
            <InnerContainer>
                {!isGeneral && <TopSection isMobile={isMobile}>
                    {isMobile && <img alt={`Download WeatherBug for your ${deviceText}`} style={{ maxWidth: '100%', maxHeight: 450, marginBottom: 16 }} src={deviceImageUrl} />}
                    <LeftContent isMobile={isMobile}>
                        <HeaderText>
                            {`Download WeatherBug for your ${deviceText}`}
                        </HeaderText>
                        {isDesktop && <SubText>
                            Get the most precise weather forecast and alerts to be prepared before severe weather strikes.
                        </SubText>}
                        <a target='_blank' rel='noopener noreferrer' href={downloadLink}>
                            <GetWeatherBugButton isMobile={isMobile}>Get WeatherBug <DownloadIcon /></GetWeatherBugButton>
                        </a>
                    </LeftContent>
                    {!isMobile && <img alt={deviceText} width='35%' src={deviceImageUrl} />}
                </TopSection>}
                <BottomSection isMobile={isMobile}>
                    <BottomContainer isMobile={isMobile}>
                        <BottomImage alt='Get real-time forecast alerts' src='images/appdownload/storm.png' />
                        <AlertIcon />
                        <BottomContainerText isMobile={isMobile}>
                            Be the first to know with real-time forecasts and severe weather alerts.
                        </BottomContainerText>
                    </BottomContainer>
                    <BottomContainer isMobile={isMobile} style={{ marginLeft: 0, marginRight: 0 }}>
                        <BottomImage alt='Multiple map layers to visual weather' src='images/appdownload/radar.png' />
                        <TwoLayersIcon />
                        <BottomContainerText isMobile={isMobile}>
                            Visualize your weather with multiple map layers.
                        </BottomContainerText>
                    </BottomContainer>
                    <BottomContainer isMobile={isMobile}>
                        <BottomImage alt='Get accurate hourly and 10-day weather forecasts' src='images/appdownload/bridge.png' />
                        <ClockIcon />
                        <BottomContainerText isMobile={isMobile}>
                            Get the most accurate hourly and 10-day weather forecasts.
                        </BottomContainerText>
                    </BottomContainer>
                </BottomSection>
                <FeedbackContainer>
                    <span>Contact Us: <a href={`mailto:${supportEmailAddress}`}>{supportEmailAddress}</a></span>
                </FeedbackContainer>
            </InnerContainer>
        </ScreenContainer>;
    }
}

const ScreenContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #FFFFFF;
    color: #333333;
    font-family: 'Source Sans Pro', 'Source Sans', 'sans-serif';
    margin-top: -39px;
    margin-bottom: -21px;
    padding-top: 57px;
    padding-bottom: 20px;
`;

const InnerContainer = styled.div`
    max-width: 1420px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const TopSection = styled.div`
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    flex-direction: ${({ isMobile }) => isMobile ? 'column' : 'row' }
`;

const LeftContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 300px;
    align-items: ${ ({ isMobile }) => isMobile ? 'center' : 'flex-start' }
`;

const HeaderText = styled.span`
    font-size: 24px;
    font-weight: bold;
    padding-bottom: 10px;
`;

const SubText = styled.span`
    font-size: 18px;
    font-weight: 500;
`;

const GetWeatherBugButton = styled.div`
    width: 180px;
    height: 52px;
    background-color: #0083C6;
    color: #FFFFFF;
    font-size: 16px;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: ${({ isMobile }) => isMobile ? '16px' : '30px' }
`;

const BottomSection = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding-top: 36px;
    width: 100%;
    flex-direction: ${ ({ isMobile }) => isMobile ? 'column' : 'row'};
    align-items: ${ ({ isMobile }) => isMobile ? 'center' : 'flex-start'}
`;

const BottomContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 464px;
    flex: 1;
    width: ${ ({ isMobile }) => isMobile ? '100%' : '33%'};
    align-items: ${ ({ isMobile }) => isMobile ? 'center' : 'flex-start'};
    margin-left: 16px;
    margin-right: 16px;
`;

const BottomContainerText = styled.span`
    font-size: 20px;
    font-weight: 500;
    padding-top: 24px;
    width: ${ ({ isMobile }) => isMobile ? '80%' : '100%'};
    padding-bottom: ${ ({ isMobile }) => isMobile ? '24px' : '0px'};
`;

const FeedbackContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    font-size: 16px;
    margin-top: 32px;
`;

const BottomImage = styled.img`
    padding-bottom: 24px;
    width: 100%;
`;

const CheckBackSoonText = styled.div`
    color: #0083C6;
    font-size: 29px;
    font-weight: bold;
    margin-top: ${({ isMobile }) => isMobile ? 16 : 30 }
`;
